import { useState } from 'react';
import { IconCopy, IconDownload } from '@tabler/icons-react';
import { StudioDialog } from '@/components/studio/StudioDialog';
import { PaletteStrip } from '@/components/studio/PaletteStrip';
import { serializeProject, type StudioDocument } from '@/lib/project-file';
import { serializePalette } from '@/lib/palette-workflow';
import { downloadTextFile } from '@/lib/file';
import type { ColorFormat } from '@/lib/color-formats';
import type { StudioNotify } from '@/types/studio';

export function PaletteExport({
  open,
  onOpenChange,
  colors,
  name,
  document,
  onCopy,
  onNotify,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  colors: string[];
  name: string;
  document: StudioDocument;
  onCopy: (text: string, label: string) => void;
  onNotify: StudioNotify;
}) {
  const [format, setFormat] = useState<'css' | 'tailwind' | 'json' | 'svg' | 'backup'>('css');
  const [colorFormat, setColorFormat] = useState<ColorFormat>('hex');
  const content =
    format === 'backup'
      ? serializeProject(document)
      : serializePalette(colors, name, format, colorFormat);
  const download = () => {
    const filename =
      (name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'palette') +
      (format === 'backup' ? '.color-studio.json' : format === 'tailwind' ? '.css' : `.${format}`);
    const ok = downloadTextFile(
      content,
      filename,
      format === 'svg'
        ? 'image/svg+xml'
        : format === 'json' || format === 'backup'
          ? 'application/json'
          : 'text/css',
    );
    onNotify(ok ? `Downloaded ${filename}.` : 'Download is unavailable. Copy the output instead.', {
      type: ok ? 'success' : 'error',
    });
  };
  return (
    <StudioDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Export palette"
      description={`${name} · ${colors.length} colors. Ready for your next project.`}
    >
      <PaletteStrip colors={colors} compact />
      <div className="export-format-tabs" aria-label="Export format">
        {(['css', 'tailwind', 'json', 'svg', 'backup'] as const).map((id) => (
          <button
            key={id}
            type="button"
            className="studio-button"
            aria-pressed={format === id}
            data-active={format === id || undefined}
            onClick={() => setFormat(id)}
          >
            {
              {
                css: 'CSS variables',
                tailwind: 'Tailwind 4',
                json: 'JSON',
                svg: 'SVG swatches',
                backup: 'Workspace backup',
              }[id]
            }
          </button>
        ))}
      </div>
      {(format === 'css' || format === 'tailwind') && (
        <label className="studio-field">
          Color format
          <select
            className="studio-select"
            value={colorFormat}
            onChange={(e) => setColorFormat(e.target.value as ColorFormat)}
          >
            {['hex', 'rgb', 'hsl', 'oklch', 'oklab'].map((id) => (
              <option key={id} value={id}>
                {id.toUpperCase()}
              </option>
            ))}
          </select>
        </label>
      )}
      {format === 'backup' && (
        <p className="studio-caption">
          Includes every saved palette, gradient, scale preset, playground, and setting. Restore it
          from the Library.
        </p>
      )}
      <textarea
        className="studio-code-output"
        aria-label="Export code"
        value={content}
        readOnly
        spellCheck={false}
      />
      <div className="studio-dialog-actions">
        <button
          type="button"
          className="studio-button"
          onClick={() => onCopy(content, 'Palette export')}
        >
          <IconCopy />
          Copy code
        </button>
        <button type="button" className="studio-button studio-button-primary" onClick={download}>
          <IconDownload />
          Download {format === 'tailwind' ? 'CSS' : format.toUpperCase()}
        </button>
      </div>
    </StudioDialog>
  );
}
